import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["appearance", "as", "buttonType", "children", "icon", "bare", "size", "title"];
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { IconButtonMixin } from '../../mixins/components/IconButtonMixin';
import { BUTTON_TYPES, BUTTON_SIZES, BUTTON_SHAPES } from '../../constants';
import { ButtonGroupContext } from '../../js/ButtonGroupContext';
import VisuallyHidden from '../VisuallyHidden/VisuallyHidden';
export var StyledIconButton = /*#__PURE__*/styled.button.withConfig({
  displayName: "StyledIconButton",
  componentId: "c11n-8-81-1__sc-1pb8vz8-0"
})(["", ""], IconButtonMixin);
/**
 * This is a icon-only button. It supports different types, different shapes and
 * a few helpers to allow you to have different asthetic variances to fit your needs.
 *
 * See also [`ButtonGroup`](#/Button%20components?id=buttongroup),
 * [`TextButton`](#/Button%20components?id=textbutton),
 * [`ToggleButton`](#/Button%20components?id=togglebutton)
 *
 * @since 8.42.0
 */

var IconButton = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var appearance = _ref.appearance,
      as = _ref.as,
      buttonType = _ref.buttonType,
      children = _ref.children,
      icon = _ref.icon,
      bare = _ref.bare,
      size = _ref.size,
      title = _ref.title,
      rest = _objectWithoutProperties(_ref, _excluded);

  var context = React.useContext(ButtonGroupContext);
  var buttonProps = Object.assign({
    as: as,
    appearance: appearance,
    buttonType: context.buttonType || buttonType,
    size: context.size || size,
    bare: bare,
    ref: ref
  }, rest); // Inputs do not allow icon children

  if (as === 'input') {
    return /*#__PURE__*/React.createElement(StyledIconButton, buttonProps, title);
  }

  return /*#__PURE__*/React.createElement(StyledIconButton, buttonProps, /*#__PURE__*/React.createElement(VisuallyHidden, null, title), /*#__PURE__*/React.createElement("span", null, icon));
});
IconButton.displayName = 'IconButton';
IconButton.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Use the styled-components
   * [polymorphic "as" prop](https://www.styled-components.com/docs/api#as-polymorphic-prop)
   * to change the HTML tag of the component.
   *
   * This can be used to apply `Button` styling to HTML anchors, inputs, and custom components.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  as: PropTypes.any,

  /**
   * The type of the button.
   * Can be inherited from a parent ButtonGroup.
   */
  buttonType: PropTypes.oneOf(Object.values(BUTTON_TYPES)),

  /**
   * Dictates the shape of the button [circle, square]
   */
  appearance: PropTypes.oneOf(Object.values(BUTTON_SHAPES)),

  /**
   * Set the button as disabled.
   */
  disabled: PropTypes.bool,

  /**
   * Add an [`Icon`](#/More%20components?id=icon) to the button.
   * _Note_ that the Icon size cannot and should not
   * be overwritten.
   */
  icon: PropTypes.node.isRequired,

  /**
   * Inverse colors for use on dark or colored backgrounds.
   */
  inverse: PropTypes.bool,

  /**
   * This removes the border on the button along with adding a transparent
   * background when the button is in the default state. Still retaining the default
   * background colors for hover, focus, and active.
   */
  bare: PropTypes.bool,

  /**
   * The size of the button.
   * Can be inherited from a parent ButtonGroup.
   */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),

  /**
   * Accessible text of the button
   */
  title: PropTypes.string.isRequired
} : {};
IconButton.defaultProps = {
  as: 'button',
  buttonType: BUTTON_TYPES.tertiary,
  appearance: BUTTON_SHAPES.square,
  disabled: undefined,
  inverse: false,
  bare: false,
  size: BUTTON_SIZES.md
};
/** @component */

export default IconButton;